import type { ReactNode } from 'react';

/**
 * A deliberately small Markdown renderer for the HR handbook.
 *
 * It builds React elements rather than an HTML string, and there is no raw-HTML
 * escape hatch: anything that is not one of the constructs below renders as
 * text. That is the point — the handbook is admin-editable, and
 * `dangerouslySetInnerHTML` over user-supplied content is how internal tools
 * grow an XSS hole. Adding a Markdown library later is fine; adding one that
 * emits HTML strings is not, unless it is paired with a sanitiser.
 *
 * Supported: # ## ### headings, - and 1. lists, | tables |, > quotes, ---
 * rules, **bold**, *italic*, `code`, [links](url), and paragraphs.
 */

/** Inline spans. Split on the delimiters in one pass so nesting cannot loop. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyPrefix}-i${i++}`;

    if (token.startsWith('**')) {
      out.push(<strong key={key} className="font-semibold text-slate-900">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      out.push(<code key={key} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const [, label, href] = /\[([^\]]+)\]\(([^)]+)\)/.exec(token) ?? [];
      // http(s) only: a javascript: or data: href would be an injection.
      const safe = href && /^https?:\/\//i.test(href) ? href : undefined;
      out.push(
        safe ? (
          <a key={key} href={safe} target="_blank" rel="noreferrer noopener" className="text-brand-700 underline">{label}</a>
        ) : (
          <span key={key}>{label}</span>
        ),
      );
    } else {
      out.push(<em key={key} className="italic">{token.slice(1, -1)}</em>);
    }
    last = match.index + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) => row.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

export function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Table: a header row followed by a |---|---| separator.
    if (line.trim().startsWith('|') && lines[i + 1]?.trim().startsWith('|') && /^\|[\s:|-]+\|$/.test(lines[i + 1].trim())) {
      const head = cells(line);
      const body: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith('|')) body.push(cells(lines[i++]));
      blocks.push(
        <div key={`t${i}`} className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-50">
                {head.map((c, x) => <th key={x} className="px-3 py-2 text-left font-semibold text-slate-700">{inline(c, `th${i}-${x}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {body.map((row, y) => (
                <tr key={y} className="border-b border-slate-100">
                  {row.map((c, x) => <td key={x} className="px-3 py-2 align-top text-slate-700">{inline(c, `td${i}-${y}-${x}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const cls = ['mt-6 mb-3 text-2xl font-bold text-slate-900', 'mt-6 mb-2 text-lg font-bold text-slate-900', 'mt-5 mb-2 text-sm font-bold uppercase tracking-wide text-slate-700', 'mt-4 mb-1 text-sm font-semibold text-slate-700'][level - 1];
      const Tag = (['h1', 'h2', 'h3', 'h4'] as const)[level - 1];
      blocks.push(<Tag key={`h${i}`} className={cls}>{inline(heading[2], `h${i}`)}</Tag>);
      i++;
      continue;
    }

    if (/^(---|\*\*\*)\s*$/.test(line)) {
      blocks.push(<hr key={`r${i}`} className="my-6 border-slate-200" />);
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) quote.push(lines[i++].replace(/^>\s?/, ''));
      blocks.push(
        <blockquote key={`q${i}`} className="my-3 border-l-4 border-brand-200 bg-brand-50/50 py-2 pl-4 text-sm text-slate-700">
          {inline(quote.join(' '), `q${i}`)}
        </blockquote>,
      );
      continue;
    }

    const bullet = /^\s*[-*]\s+/;
    const numbered = /^\s*\d+\.\s+/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const re = ordered ? numbered : bullet;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i])) items.push(lines[i++].replace(re, ''));
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={`l${i}`} className={`my-3 space-y-1.5 pl-5 text-sm text-slate-700 ${ordered ? 'list-decimal' : 'list-disc'}`}>
          {items.map((item, x) => <li key={x}>{inline(item, `li${i}-${x}`)}</li>)}
        </Tag>,
      );
      continue;
    }

    // Paragraph: consecutive plain lines, joined.
    const para: string[] = [];
    while (
      i < lines.length && lines[i].trim() &&
      !/^(#{1,4}\s|>|---|\*\*\*)/.test(lines[i]) &&
      !bullet.test(lines[i]) && !numbered.test(lines[i]) && !lines[i].trim().startsWith('|')
    ) para.push(lines[i++]);
    blocks.push(<p key={`p${i}`} className="my-3 text-sm leading-relaxed text-slate-700">{inline(para.join(' '), `p${i}`)}</p>);
  }

  return <div>{blocks}</div>;
}
